import React, { useState, useEffect } from "react";
// Assuming you put your firebase utils in a file like 'db/firebaseUtils.js'
// **You must add the addSubscriberEmail function to this file**
import { fetchAllCategories, fetchLatestCategory, addSubscriberEmail } from '../db/firebaseUtils'; 
import logo from "../assets/logo_light.webp";
import { ArrowRight, Facebook, Instagram, Twitter } from 'lucide-react'; // Added social icons

const Footer = () => {
    // State for data fetching
    const [categories, setCategories] = useState([]); 
    const [latestCategory, setLatestCategory] = useState(null);

    // State for newsletter form
    const [email, setEmail] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [message, setMessage] = useState(''); // Success or error message

    // Load data (Categories)
    useEffect(() => {
        const loadData = async () => {
            try {
                const [fetchedCategories, category] = await Promise.all([
                    fetchAllCategories(),
                    fetchLatestCategory()
                ]);
                setCategories(fetchedCategories); 
                if (category) {
                    setLatestCategory(category);
                }
            } catch (error) {
                console.error("Failed to fetch footer data:", error);
            }
        };
        loadData();
    }, []);

    // Form Submission Handler
    const handleSubscribe = async (e) => {
        e.preventDefault(); // Prevent default form submission
        if (!email.trim() || isSubmitting) return;

        setIsSubmitting(true);
        setMessage(''); // Clear previous messages

        try {
            // **Call the new utility function to save the email**
            await addSubscriberEmail(email); 

            // Success feedback
            setMessage('Thank you for subscribing! 🎉');
            setEmail(''); // Clear the input field

        } catch (error) {
            console.error("Newsletter subscription failed:", error);
            // Error feedback
            setMessage('Subscription failed. Please try again.');
        } finally {
            setIsSubmitting(false);

            // Optional: Clear the message after a few seconds
            setTimeout(() => setMessage(''), 5000); 
        }
    };

    // Get the first 4 categories for the minimal list
    const shopLinks = categories.slice(0, 4);

    // ... (Your existing component structure) ...

    return (
        <footer className="bg-charcoal text-offwhite">
            <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6 grid grid-cols-1 md:grid-cols-4 gap-6">
                
                {/* Logo & Brand Description */}
                <div>
                    <img src={logo} alt="Leatherhood Logo" className="h-12 mb-3" />
                    <p className="text-sm text-beige leading-relaxed">
                        Premium handcrafted leather goods for timeless style. 
                        Discover bags, wallets, belts, and more with Leatherhood.
                    </p>
                </div>

                {/* Shop Links */}
                <div>
                    <h3 className="text-md font-semibold text-tan mb-3 border-b border-taupe pb-1 inline-block">
                        Shop
                    </h3>
                    <ul className="space-y-1 text-sm">
                        {/* Dynamic Links: Top 4 Categories */}
                        {shopLinks.map(category => (
                            <li key={category.id}>
                                <a 
                                    href={`/shop/category/${category.id}`} 
                                    className="text-gray-300 hover:text-tan transition-colors"
                                >
                                    {category.name}
                                </a>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Company Links */}
                <div>
                    <h3 className="text-md font-semibold text-tan mb-3 border-b border-taupe pb-1 inline-block">
                        Company
                    </h3>
                    <ul className="space-y-1 text-sm">
                        <li><a href="/about" className="text-gray-300 hover:text-tan transition-colors">About Us</a></li>
                        <li><a href="/contact" className="text-gray-300 hover:text-tan transition-colors">Contact</a></li>
                    </ul>
                </div>

                {/* Newsletter (Subscription Logic Updated) */}
                <div>
                    <h3 className="text-md font-semibold text-tan mb-3 border-b border-taupe pb-1 inline-block">
                        Stay Updated
                    </h3>
                    <p className="text-sm text-beige mb-2">
                        Sign up to receive exclusive offers and news from Leatherhood.
                    </p>
                    {/* The form now uses handleSubscribe and state */}
                    <form className="flex" onSubmit={handleSubscribe}>
                        <input 
                            type="email" 
                            placeholder="Enter your email" 
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            className="px-3 py-2 w-full rounded-l-md text-charcoal text-sm focus:outline-none"
                            disabled={isSubmitting} // Disable during submission
                        />
                        <button 
                            type="submit" 
                            className={`px-4 py-2 rounded-r-md text-sm font-semibold transition ${
                                isSubmitting 
                                    ? 'bg-taupe text-gray-500 cursor-not-allowed' 
                                    : 'bg-tan text-charcoal hover:bg-primary'
                            }`}
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? 'Sending...' : 'Subscribe'}
                        </button>
                    </form>
                    {/* Feedback Message */}
                    {message && (
                        <p className={`mt-2 text-sm ${message.includes('success') || message.includes('Thank') ? 'text-green-400' : 'text-red-400'}`}>
                            {message}
                        </p>
                    )}
                </div>
            </div>
            
            {/* Bottom Bar */}
            <div className="border-t border-taupe mt-4 py-2">
                <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row justify-between items-center text-xs text-beige">
                    <p>© {new Date().getFullYear()} Leatherhood. All Rights Reserved.</p>
                    <div className="flex space-x-4 mt-2 md:mt-0">
                        <a href="#" className="hover:text-tan transition">Facebook</a>
                        <a href="https://www.instagram.com/leatherhoodpk/" className="hover:text-tan transition">Instagram</a>
                        <a href="#" className="hover:text-tan transition">TikTok</a>
                        <a href="http://www.youtube.com/@LeatherHoodPK" className="hover:text-tan transition">YouTube</a>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;